// Shared channel-intelligence helpers — source of truth for Channels Stock is
// overview_source → "Channels Stock" (Col V). DRR for "Offline" channel maps to "B2B DRR".

export const CHANNEL_DRR_KEY = {
  AZ: 'AZ DRR',
  FK: 'FK DRR',
  Blinkit: 'Blinkit DRR',
  'W.in': 'W.in DRR',
  Zepto: 'Zepto DRR',
  Meesho: 'Meesho DRR',
  'FK Mins': 'FK Mins DRR',
  'AZ Now': 'AZ Now DRR',
  Offline: 'B2B DRR',
};

export const CHANNEL_LIST = ['AZ', 'FK', 'Blinkit', 'W.in', 'Zepto', 'Meesho', 'FK Mins', 'AZ Now', 'Offline'];

// User-facing channel labels (used across cards, tables, filters, charts).
export const CHANNEL_DISPLAY = {
  AZ: 'Amazon',
  FK: 'Flipkart',
  Blinkit: 'Blinkit',
  'W.in': 'W.in',
  Zepto: 'Zepto',
  Meesho: 'Meesho',
  'FK Mins': 'Flipkart Minutes',
  'AZ Now': 'Amazon Now',
  Offline: 'Offline',
  Tata: 'Tata',
};
export const dn = (ch) => CHANNEL_DISPLAY[ch] || ch;

export const parseNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return value;
  const cleaned = value.toString().trim().replace(/,/g, '').replace(/[^0-9.-]/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

export const findColumnIndex = (headers, possibleNames) => {
  const lower = headers.map(h => (h || '').toString().trim().toLowerCase().replace(/\s+/g, ' '));
  for (const name of possibleNames) {
    const needle = name.toLowerCase().replace(/\s+/g, ' ');
    for (let i = 0; i < lower.length; i++) {
      if (lower[i].includes(needle)) return i;
    }
  }
  return -1;
};

// Return ALL header indices whose lower-cased/collapsed value contains ANY of the given patterns.
// Used for channels whose stock is split across multiple columns (e.g. W.in = MCF + W.in + W.in FC).
export const findAllColumnIndices = (headers, possibleNames) => {
  const lower = headers.map(h => (h || '').toString().trim().toLowerCase().replace(/\s+/g, ' '));
  const found = new Set();
  possibleNames.forEach(name => {
    const needle = name.toLowerCase().replace(/\s+/g, ' ');
    lower.forEach((h, i) => { if (h.includes(needle)) found.add(i); });
  });
  return [...found];
};

// Exact (case-insensitive, whitespace-normalised) header match — no partial/substring.
// Returns indices (array) for all exact-equal occurrences; use for channels requiring strict mapping.
export const findExactColumnIndices = (headers, exactNames) => {
  const norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
  const normHeaders = headers.map(norm);
  const targets = exactNames.map(norm);
  const found = [];
  normHeaders.forEach((h, i) => { if (targets.includes(h)) found.push(i); });
  return found;
};

// Case-insensitive / whitespace-normalised object lookup
export const getCI = (obj, key) => {
  if (!obj || !key) return undefined;
  const needle = key.toLowerCase().trim().replace(/\s+/g, ' ');
  for (const k of Object.keys(obj)) {
    if (k.toLowerCase().trim().replace(/\s+/g, ' ') === needle) return obj[k];
  }
  return undefined;
};

// Stocks-tab channel column mapping (per channel: stock, in-transit, value)
// W.in is special: its stock = MCF Stock + W.in Stock + W.in FC Stock (sum across matches).
export const buildStocksColumnMap = (headers) => ({
  sku: findColumnIndex(headers, ['title', 'sku', 'product name', 'asin']),
  AZ:      { stock: findColumnIndex(headers, ['fba stock', 'amazon stock', 'az stock']),        transit: findColumnIndex(headers, ['fba in-transit', 'fba transit', 'amazon in-transit']),     value: findColumnIndex(headers, ['fba stock value', 'fba value', 'amazon value']) },
  FK:      { stock: findColumnIndex(headers, ['fbf stock', 'fbf', 'flipkart stock', 'fk stock']), transit: findColumnIndex(headers, ['fbf in-transit', 'fbf transit', 'flipkart in-transit']), value: findColumnIndex(headers, ['fbf stock value', 'fbf value', 'flipkart value']) },
  Blinkit: { stock: findColumnIndex(headers, ['blinkit stock', 'blinkit']),                     transit: findColumnIndex(headers, ['blinkit in-transit', 'blinkit transit']),                  value: findColumnIndex(headers, ['blinkit stock value', 'blinkit value']) },
  'W.in':  {
    // STRICT exact-match (case-insensitive, trimmed): ONLY "MCF Stock" and "W.in FC Stock".
    // No fuzzy, no partial — prevents including "W.in Stock", "Wepik", values, etc.
    stockMany:   findExactColumnIndices(headers, ['MCF Stock', 'W.in FC Stock']),
    transitMany: findExactColumnIndices(headers, ['MCF In-Transit', 'W.in FC In-Transit']),
    valueMany:   findExactColumnIndices(headers, ['MCF Stock Value', 'W.in FC Stock Value']),
  },
  Zepto:   { stock: findColumnIndex(headers, ['zepto stock', 'zepto']),                         transit: findColumnIndex(headers, ['zepto in-transit', 'zepto transit']),                      value: findColumnIndex(headers, ['zepto stock value', 'zepto value']) },
  Meesho:  { stock: findColumnIndex(headers, ['meesho stock', 'meesho']),                       transit: findColumnIndex(headers, ['meesho in-transit', 'meesho transit']),                    value: findColumnIndex(headers, ['meesho stock value', 'meesho value']) },
  'FK Mins': { stock: findColumnIndex(headers, ['fk mins stock', 'fk mins', 'fkmins', 'flipkart mins']), transit: findColumnIndex(headers, ['fk mins in-transit', 'fkmins in-transit']),        value: findColumnIndex(headers, ['fk mins value', 'fkmins value']) },
  // AZ Now: Stocks tab labels the value column ambiguously as "Stock value"
  // (shared with Tata). We pick up value only when ops relabels it uniquely —
  // stock + in-transit render correctly today; value stays 0 until then.
  'AZ Now': { stock: findColumnIndex(headers, ['az now stock', 'amazon now stock', 'az now', 'amazon now']), transit: findColumnIndex(headers, ['az now in-transit', 'az now transit', 'amazon now in-transit', 'amazon now transit']), value: findColumnIndex(headers, ['az now stock value', 'az now value', 'amazon now stock value', 'amazon now value']) },
  Offline: { stock: findColumnIndex(headers, ['offline stock', 'offline', 'b2b stock', 'b2b']),  transit: findColumnIndex(headers, ['offline in-transit', 'offline transit', 'b2b in-transit']), value: findColumnIndex(headers, ['offline stock value', 'offline value', 'b2b value']) },
});

// Overview-tab map for Channels Stock (Col V) -- SOURCE OF TRUTH
// If the dedicated "Channels Stock" column is absent, derive as
// (Total Current Stock) - (F2 Current Stock) - (F2 Pre-Packed Stock) and flag as derived.
export const buildOverviewChannelsStockMap = (overviewRows) => {
  const out = {
    map: {}, total: 0,
    skuIdx: -1, colIdx: -1,
    derived: false, source: 'channels_stock_col',
  };
  if (!overviewRows || overviewRows.length < 2) {
    console.error('[channel-intel] overview_source rows missing');
    return out;
  }
  const headers = overviewRows[0];
  out.skuIdx = findColumnIndex(headers, ['sku', 'title', 'product name']);
  out.colIdx = findColumnIndex(headers, ['channels stock', 'channel stock']);

  if (out.skuIdx === -1) {
    console.error('[channel-intel] SKU column missing in overview_source');
    return out;
  }

  if (out.colIdx !== -1) {
    overviewRows.slice(1).forEach(row => {
      const sku = (row[out.skuIdx] || '').toString().trim();
      if (!sku) return;
      const v = parseNumber(row[out.colIdx]);
      out.map[sku] = v;
      out.total += v;
    });
    return out;
  }

  // Fallback: derive from Total Current Stock minus F2 components
  const tcsIdx = findColumnIndex(headers, ['total current stock', 'total stock']);
  const f2CurIdx = findColumnIndex(headers, ['f2 current stock']);
  const f2PpIdx = findColumnIndex(headers, ['f2 pre-packed stock', 'f2 prepacked stock', 'f2 pre packed stock']);
  if (tcsIdx === -1 || f2CurIdx === -1 || f2PpIdx === -1) {
    console.error(
      '[channel-intel] "Channels Stock" column missing in overview_source AND insufficient columns to derive it (need Total Current Stock + F2 Current + F2 Pre-Packed).'
    );
    return out;
  }
  console.warn(
    '[channel-intel] "Channels Stock" (Col V) not found in overview_source. Deriving from Total Current Stock − F2 Current − F2 Pre-Packed. Add the dedicated column at Col V to enable source-of-truth mode.'
  );
  out.derived = true;
  out.source = 'derived_tcs_minus_f2';
  overviewRows.slice(1).forEach(row => {
    const sku = (row[out.skuIdx] || '').toString().trim();
    if (!sku) return;
    const v = Math.max(
      0,
      parseNumber(row[tcsIdx]) - parseNumber(row[f2CurIdx]) - parseNumber(row[f2PpIdx])
    );
    out.map[sku] = v;
    out.total += v;
  });
  return out;
};

// Build per-SKU rows from Stocks tab + allocation tab; channelsStock is pulled from overview map.
export const buildSkuIntelligence = ({ overviewRows, stocksRows, allocationRows }) => {
  const overview = buildOverviewChannelsStockMap(overviewRows);

  if (!stocksRows || stocksRows.length < 2 || !allocationRows || allocationRows.length < 2) {
    return { skus: [], overview };
  }

  const stocksHeaders = stocksRows[0];
  const allocHeaders = allocationRows[0];
  const stocksMap = buildStocksColumnMap(stocksHeaders);

  if (stocksMap.sku === -1) {
    console.error('[channel-intel] SKU column missing in Stocks tab');
    return { skus: [], overview };
  }

  // Report which exact columns were matched for W.in (strict mode)
  const winStockIdx = stocksMap['W.in'].stockMany || [];
  if (winStockIdx.length === 0) {
    console.error('[channel-intel] W.in strict mapping — neither "MCF Stock" nor "W.in FC Stock" found in Stocks tab headers');
  } else {
    const matched = winStockIdx.map(i => `"${stocksHeaders[i]}"`).join(', ');
    console.log(`[channel-intel] W.in strict stock columns matched: ${matched}`);
  }

  // Allocation: SKU → row dict
  const allocationBySku = {};
  allocationRows.slice(1).forEach(row => {
    const obj = {};
    allocHeaders.forEach((h, i) => { obj[h] = row[i] !== undefined ? row[i] : ''; });
    const sku = (obj.SKU || obj.TITLE || obj['Product Name'] || '').toString().trim();
    if (sku) allocationBySku[sku] = obj;
  });

  // Sanity: warn on missing DRR columns
  const sampleAlloc = allocationBySku[Object.keys(allocationBySku)[0]] || {};
  const missingDRR = Object.entries(CHANNEL_DRR_KEY)
    .filter(([, key]) => getCI(sampleAlloc, key) === undefined)
    .map(([ch, key]) => `${ch} → "${key}"`);
  if (missingDRR.length) console.error('[channel-intel] Missing DRR cols in allocation_source:', missingDRR);

  const skus = [];
  stocksRows.slice(1).forEach(row => {
    const sku = (row[stocksMap.sku] || '').toString().trim();
    if (!sku) return;

    const channels = {};
    CHANNEL_LIST.forEach(ch => {
      const m = stocksMap[ch];
      const sumIdx = (indices) => (indices || []).reduce((s, i) => s + parseNumber(row[i]), 0);
      const stock = m.stockMany ? sumIdx(m.stockMany) : (m.stock !== -1 ? parseNumber(row[m.stock]) : 0);
      const transit = m.transitMany ? sumIdx(m.transitMany) : (m.transit !== -1 ? parseNumber(row[m.transit]) : 0);
      const value = m.valueMany ? sumIdx(m.valueMany) : (m.value !== -1 ? parseNumber(row[m.value]) : 0);
      const drrKey = CHANNEL_DRR_KEY[ch];
      const drr = parseNumber(getCI(allocationBySku[sku] || {}, drrKey));
      const totalStock = stock + transit;
      // DOH and Sales:Stock now use *available* stock (excluding in-transit) to
      // reflect what's actually shippable today. totalStock is retained for any
      // place that explicitly needs stock + in-transit.
      const doh = drr > 0 && stock > 0 ? stock / drr : 0;
      const sales30 = drr * 30;
      const salesStockRatio = stock > 0 && drr > 0 ? sales30 / stock : 0;
      // The Stocks sheet `value` column is computed over stock + transit. Pro-rate
      // it down to *available* stock so the Channel Intelligence cards show the
      // shippable inventory's monetary value, not pipeline value. `totalValue`
      // preserves the original sum (including transit) for any place that needs it.
      const totalValue = value;
      const availableValue = totalStock > 0 ? value * (stock / totalStock) : 0;

      // W.in-specific validations
      if (ch === 'W.in') {
        if (doh > 365) {
          console.error(`[channel-intel] W.in aggregation issue — SKU "${sku}" DOH=${Math.round(doh)} (stock=${stock}, drr=${drr})`);
        }
        if (drr > 0 && salesStockRatio === 0) {
          console.warn(`[channel-intel] W.in Sales:Stock=0 while DRR>0 — SKU "${sku}" (stock=${stock}, drr=${drr})`);
        }
      }

      channels[ch] = { stock, transit, totalStock, value: availableValue, totalValue, drr, doh, sales30, salesStockRatio };
    });

    // SOURCE OF TRUTH for aggregate channelsStock
    const channelsStock = overview.map[sku];
    if (channelsStock === undefined) {
      console.error(`[channel-intel] SKU "${sku}" missing in overview_source Channels Stock`);
    }

    skus.push({ sku, channels, channelsStock: channelsStock || 0 });
  });

  return { skus, overview };
};

// Aggregate per-channel KPIs from the SKU list. No recomputation of Channels Stock here.
// `stock` is *available* stock (excluding in-transit) — used for DOH, Stock:Sales,
// and critical-SKU classification. `value` is the pro-rated available value
// (Σ per-SKU availableValue). `transit`, `totalStock`, `totalValue` are exposed
// separately for cards that need to show full pipeline.
export const aggregateChannelKpis = (skus) => {
  const agg = {};
  CHANNEL_LIST.forEach(ch => {
    const stock = skus.reduce((s, x) => s + x.channels[ch].stock, 0);
    const transit = skus.reduce((s, x) => s + (x.channels[ch].transit || 0), 0);
    const totalStock = stock + transit;
    const value = skus.reduce((s, x) => s + (x.channels[ch].value || 0), 0);
    const totalValue = skus.reduce((s, x) => s + (x.channels[ch].totalValue || 0), 0);
    const drr = skus.reduce((s, x) => s + x.channels[ch].drr, 0);
    const sales30 = drr * 30;
    const doh = stock > 0 && drr > 0 ? stock / drr : 0;
    const salesStockRatio = stock > 0 ? sales30 / stock : 0;
    agg[ch] = { channel: ch, stock, transit, totalStock, value, totalValue, drr, sales30, doh, salesStockRatio, status: classifyStatus(doh, stock) };
  });

  // Aggregate-level W.in sanity checks
  const win = agg['W.in'];
  if (win) {
    if (win.doh > 365) {
      console.error(`[channel-intel] W.in aggregation issue — aggregate DOH=${Math.round(win.doh)} (stock=${win.stock}, drr=${win.drr})`);
    }
    if (win.drr > 0 && win.salesStockRatio === 0) {
      console.warn(`[channel-intel] W.in aggregate Sales:Stock=0 while DRR>0 (stock=${win.stock}, drr=${win.drr})`);
    }
  }
  return agg;
};

// DOH-based status classification
export const classifyStatus = (doh, stock) => {
  if (!stock || stock === 0) return { label: 'Critical', tone: 'red' };
  if (doh < 7) return { label: 'Critical', tone: 'red' };
  if (doh < 15) return { label: 'Risk', tone: 'orange' };
  if (doh <= 45) return { label: 'Healthy', tone: 'green' };
  return { label: 'Excess', tone: 'blue' };
};

export const STATUS_STYLES = {
  red:    'bg-[#FEE2E2] text-[#B91C1C]',
  orange: 'bg-[#FFEDD5] text-[#C2410C]',
  green:  'bg-[#DCFCE7] text-[#15803D]',
  blue:   'bg-[#DBEAFE] text-[#1D4ED8]',
  slate:  'bg-[#F1F5F9] text-[#64748B]',
};

export const DOH_CELL = (doh, stock) => {
  if (!stock || stock === 0) return 'bg-[#F1F5F9] text-[#64748B]';
  if (doh < 7) return 'bg-[#FEE2E2] text-[#B91C1C]';
  if (doh < 15) return 'bg-[#FFEDD5] text-[#C2410C]';
  if (doh <= 45) return 'bg-[#DCFCE7] text-[#15803D]';
  return 'bg-[#DBEAFE] text-[#1D4ED8]';
};
